/**
 * @module engine/export/json
 * @summary Save / load the document as JSON.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3746-3761.
 */
import { S, emit, markDirty } from '../state';
import { pushUndo } from '../history';
import { applyDocument, clearAutoSave, markSaved, serialize } from '../persistence';
import { dlText } from '../../lib/download';
import { toast } from '../../lib/toast';
import type { PfdDocument } from '../../types';

export { serialize } from '../persistence';

/** The current document name (used for filenames). */
function docName(): string {
  return S.docMeta.docName || 'PFD';
}

/** Save the document as a downloaded JSON file. */
export function saveJson(): void {
  const n = docName();
  dlText(JSON.stringify(serialize(), null, 2), `JCC_PFD_${n}.json`, 'application/json');
  // Mark as saved so the exit guard won't prompt until the next edit.
  clearAutoSave();
  markSaved();
  toast('Saved as JCC_PFD_' + n + '.json');
}

/** Load a document from a JSON File (e.g. from an <input type=file>). */
export function loadJsonFile(file: File): void {
  const rdr = new FileReader();
  rdr.onload = (ev) => {
    try {
      const d = JSON.parse(String(ev.target?.result)) as Partial<PfdDocument>;
      // A document with no shapes array is not one of ours; better to say so
      // than to silently replace the open drawing with nothing.
      if (!Array.isArray(d.shapes)) throw new Error('not a PFD document');
      pushUndo();
      applyDocument(d);
      S.selected = [];
      // The document now mirrors a file on disk — nothing unsaved yet.
      markSaved();
      markDirty();
      emit('doc');
      emit('selection');
      toast('Loaded ' + file.name);
    } catch {
      alert('Invalid JSON file');
    }
  };
  rdr.readAsText(file);
}
